import React, { Component } from 'react';

import codemirror from 'codemirror';
import Javascript from 'codemirror/mode/javascript/javascript';
import Yaml from 'codemirror/mode/yaml/yaml';
import FoldGutter from 'codemirror/addon/fold/foldgutter';
import FoldCode from 'codemirror/addon/fold/foldcode';
import FoldIndent from 'codemirror/addon/fold/indent-fold';
import FoldComment from 'codemirror/addon/fold/comment-fold';
import Linter from 'codemirror/addon/lint/lint';
import Hinter from 'codemirror/addon/hint/show-hint';
import Searcher from 'codemirror/addon/search/searchcursor';

import Bespin from 'codemirror/theme/bespin.css';
import Base16Light from 'codemirror/theme/base16-light.css';
import DefaultCodeMirrorStyle from 'codemirror/lib/codemirror.css';
import FoldGutterStyle from 'codemirror/addon/fold/foldgutter.css';
import LintStyle from 'codemirror/addon/lint/lint.css';
import DefaultStyle from '../../styles/default.scss';

// a codemirror-compatible linter which calls a 'validate' method dynamically
// attached to the codemirror instance
// the validator should return an array of {message, severity} objects and
// can optionally pass line and column information via to: {line, ch}, from: {line, ch}
const validation_linter = async (text, cb, options, ctx) => {
  const annotations = [];
  if (!ctx.validate) {
    log.info('no validator...', options);
    return cb([]);
  }
  try {
    const {doc, errors = []} = await ctx.validate(text);
    ctx.__last_doc = doc;

    annotations.extend(errors);
  }
  catch(err) {
    //console.log(err, err.stack);
    const loc = err.mark || {line: 0, column: 0};
    annotations.push({
      from: codemirror.Pos(loc.line, loc.column),
      to: codemirror.Pos(loc.line, loc.column),
      message: err.message,
      severity: 'error'
    });
  }
  cb(annotations);
};
validation_linter.async = true;

codemirror.registerHelper('lint', 'yaml', validation_linter);

// takes a template and set of predefined substitutions and returns a
// codemirror template substituion hinter, which can be passed as the 'hint'
// property to codemirror completion objects
const template_completer = (_template, predefs) => (ctx, data, self) => {
  const {template, params} = _template;

  // substitute the injected predefs into the template string
  let text = template;
  for (const [name, value] of Object.entries(predefs)) {
    // WHAT?! - can't believe this is faster... http://stackoverflow.com/a/1145525
    text = text.split(`\${${name}}`).join(value);
  }

  // replace the current completion context text with the template text
  ctx.replaceRange(text, data.from, data.to, 'complete');

  // track the current endpoint before substituting any text - need to jump here at end
  const end = {line: data.from.line + text.split('\n').length - 1, ch: 1000};
  const end_mark = ctx.setBookmark(end);

  // for each specified parameter, generate the corresponding tag for finding it
  // in the expanded template string
  const param_tags = Object.keys(params).map(param => `\${${param}}`);

  // key map ref for removing the template insertion-mode overrides
  let replacement_key_map = null;

  // cleanup routine called at cancellation or end of the template substitutions
  // resets the key map and jumps to the bookmark if it still exists
  const cleanup = () => {
    const jump_to = end_mark.find();
    if (jump_to) {
      ctx.setCursor(jump_to);
      end_mark.clear();
    }
    ctx.removeKeyMap(replacement_key_map);
  };

  // each invocation will pop the next parameter substitution off of the list,
  // search for the corresponding placeholder in the template, and then
  // if it finds it, set the current text selection accordingly
  const replace_next_tag = () => {
    const tag = param_tags.shift();
    if (!tag) {
      return cleanup();
    }

    const cursor = ctx.getSearchCursor(tag, data.from);
    const res = cursor.find();
    if (res) {
      ctx.setSelection(cursor.from(), cursor.to());
    }
    else {
      end_mark.clear();
      ctx.removeKeyMap(replacement_key_map);
    }
  };

  // during the template insertion, tab and enter will jump to the next
  // template substitution point, and esc will cancel the template completion
  // the final jump is to the original end of the template string
  replacement_key_map = {
    Tab() {
      replace_next_tag();
    },
    Enter() {
      replace_next_tag();
    },
    Esc() {
      end_mark.clear();
      ctx.removeKeyMap(replacement_key_map);
    }
  };

  // sets up the key map and begins substitutions
  ctx.addKeyMap(replacement_key_map);
  replace_next_tag();
};

class CodeMirror extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ctx: null,
      value: props.value,
      focused: false
    };
    this.renderCount = 0;
  }

  static CodeMirror() {
    return codemirror;
  }

  shouldComponentUpdate(newProps, newState) {
    if (!this.state.focused && newProps.value !== this.props.value) {
      this.renderCount = 1;
      this.updateValue(newProps.value);
    } else if(this.renderCount === 0 && this.props.readOnly && this.props.renderOnce) {
      this.renderCount = 1;
      return true;
    }
    return false;
  }

  onMount(elem) {
    if (!elem) {
      this.cm = null;
      return;
    }
    else if (!this.cm) {
      // possible gutters - ['CodeMirror-lint-markers', 'CodeMirror-linenumbers', 'CodeMirror-foldgutter'],

      const gutters = [];
      const {lint, lineNumbers, foldable, readOnly, theme} = this.props;
      if (lint) {
        gutters.push('CodeMirror-lint-markers');
      }
      if (lineNumbers) {
        gutters.push('CodeMirror-linenumbers');
      }
      if (foldable) {
        gutters.push('CodeMirror-foldgutter');
      }

      const cm = codemirror.fromTextArea(elem, _.merge({}, this.props.options, {gutters, lineNumbers, lint, readOnly, theme}));
      cm.on('change', this.valueChanged.bind(this));
      cm.on('focus', this.focusChanged.bind(this, true));
      cm.on('blur', this.focusChanged.bind(this, false));
      cm.on('keydown', this.keyDown.bind(this));
      cm.on('cursorActivity', this.cursorMoved.bind(this));
      cm.validate = this.props.validate; // set our validating function
      this.cm = cm;
    }
  }

  componentWillUnmount() {
    if (this.cm) {
      this.cm.toTextArea();
    }
  }

  getCodeMirror() {
    return this.cm;
  }

  focus() {
    this.cm && this.cm.focus();
  }

  focusChanged(focused) {
    this.setState({
      focused
    });

    if (focused) {
      this.props.onFocus && this.props.onFocus();
      this.showAutocomplete();
    }
    else {
      this.props.onBlur && this.props.onBlur();
    }
  }

  valueChanged(doc, change) {
    const newValue = doc.getValue();
    this.setState({
      value: newValue
    });
    this.props.onDocChange && this.props.onDocChange(newValue);
  }

  // iterate through the document in reverse and determine the current path
  getPathAt(position) {
    const doc = this.cm;

    let last_padding = position.ch;
    let array_position = 0;
    const path = [];
    for (let line = position.line; line >= 0; --line) {
      let content = doc.getLine(line);
      const ltrimmed = content.trimLeft();
      let padding = content.length - ltrimmed.length;

      // we can skip any initial lines which are indented the same as our cursor
      if (padding === position.ch) {
        continue;
      }

      // ignore extra indentation levels
      if (padding > last_padding) {
        continue;
      }

      // either we have same or greater indentation - now check if this is an array element
      if (ltrimmed.startsWith('-')) {
        ++array_position;
        content = ltrimmed.substr(2) || '';
        padding += 2;
      }
      else {
        if (array_position > 0) {
          path.push(array_position);
          array_position = 0;
        }
      }

      // if we have the same indentation, continue instead of looking for keys
      if (padding >= last_padding) {
        continue;
      }

      const [field, ...rest] = content.split(/:(?:\s+|$)/);
      if (!field || rest.length === 0) continue;

      path.push(field.trim());
      last_padding = padding;

      if (padding === 0) {
        break;
      }
    }
    return path.reverse().filter(val => val !== '');
  }

  // TODO: Refactor
  getCompletions(ctx, cb, options) {
    const self = this;
    const start = options.start;
    const path = this.getPathAt(start);

    const current = ctx.getCursor();
    const line = ctx.getLine(current.line);

    const content = line.substr(options.start.ch);
    const indent = line.length - line.trimLeft().length;
    const padding = ' '.repeat(indent);

    const {props, completions: templates, values} = this.props.complete(path);
    const checker = new RegExp(content, 'i');
    let property_completions = Object.keys(props).filter(prop => checker.test(prop) || checker.test(props[prop].description));
    let template_completions = Object.keys(templates).filter(name => checker.test(name) || checker.test(templates[name].description));
    let value_completions = values;

    property_completions = property_completions.map(prop => {
      const schema = props[prop];
      const description = schema.description ? ` - ${schema.description}` : '';
      switch (schema.type) {
        case 'object':
          return {
            prop,
            text: `${prop}:\n${padding}  `,
            displayText: `${prop}${description}`,
            className: 'CodeMirror-hint-property'
          };
        case 'array':
          return {
            prop,
            text: `${prop}:\n${padding}  - `,
            displayText: `${prop}${description}`,
            className: 'CodeMirror-hint-property'
          };
        default: {
          return {
            prop,
            text: `${prop}: `,
            displayText: `${prop}${description}`,
            className: 'CodeMirror-hint-property'
          };
        }
      }
    });

    template_completions = template_completions.map(name => {
      let {description} = templates[name];
      description = description ? ` - ${description}` : '';
      return {
        text: '',
        displayText: `{${name}}${description}`,
        className: 'CodeMirror-hint-template',
        hint: template_completer(templates[name], {indent: padding})
      };
    }).flatten();

    value_completions = value_completions.map(schema => {
      const description = schema.description ? ` - ${schema.description}` : '';
      if (schema.enum) {
        return schema.enum.filter(val => checker.test(val))
          .map(val => ({
            text: `${val}\n${padding}`,
            displayText: val,
            className: 'CodeMirror-hint-value'
          }));
      }
      switch (schema.type) {
        case 'array':
          return {
            text: '- ',
            displayText: '-',
            className: 'CodeMirror-hint-value'
          };
        default: {
          return {
            text: `${content}\n${padding}`,
            displayText: `<${schema.format || schema.type}>${description}`,
            className: 'CodeMirror-hint-value'
          };
        }
      }
    }).flatten();

    const current_node = (path.length > 0 ? _.get(ctx.__last_doc, path) : ctx.__last_doc) || {};
    property_completions = property_completions.filter(completion => !current_node.hasOwnProperty(completion.prop));

    const completions = [...property_completions, ...template_completions, ...value_completions];

    cb({
      list: completions,
      from: start,
      to: current
    });
  }

  showAutocomplete() {
    const hinter = this.getCompletions.bind(this);
    hinter.async = true;

    const cursor = this.cm.getCursor();
    const line = this.cm.getLine(cursor.line);

    // only show autocomplete for empty lines, value completions, or array elements
    if (!/^\s*$/.test(line) && !/^\s*-\s*$/.test(line) && !/.*\S+\s*:\s+$/.test(line)) {
      return;
    }

    this.cm.showHint({
      hint: hinter,
      completeSingle: false,
      start: cursor
    });
  }

  cursorMoved(ctx) {
    const self = this;
    clearTimeout(this.state.ctx);
    self.setState({
      ctx: setTimeout(() => {
        const current_path = self.getPathAt(ctx.getCursor());
        self.props.onPathChange && self.props.onPathChange(current_path);
        ctx.focus();
      }, 200)
    });
  }

  keyDown(ctx, evt) {
    if (ctx.state.completionActive) {
      // if we have an active completion window, disable tab completion...
      if (evt.keyCode === 9) {
        ctx.state.completionActive.widget && ctx.state.completionActive.widget.disable();
      }
    }

    // ctrl-space
    if (evt.keyCode === 32 && evt.ctrlKey) {
      this.showAutocomplete();
    }
    else {
      // <backspace> / <tab> / <return> / <space>
      if (evt.keyCode === 8 || evt.keyCode === 9 || evt.keyCode === 13 || evt.keyCode === 32) {
        const autocomplete = this.showAutocomplete.bind(this);
        // need to execute after delay so that whitespace insertion propagation finishes first...
        setTimeout(autocomplete, 200);
      }
    }

    // : / .  evt.keyCode === 186 || evt.keyCode === 190
  }

  // forcibly update the value
  updateValue(value) {
    this.cm && this.cm.setValue(value);
  }

  render() {
    const onMount = this.onMount.bind(this);
    return <div className={this.props.className} style={this.props.style}>
      <textarea ref='textarea'
        name={this.props.path}
        defaultValue={this.props.value}
        autoComplete='off'
        ref={onMount}
      />
    </div>;
  }
}

CodeMirror.defaultProps = {
  content: '',
  async: true,
  readOnly: false,
  lint: true,
  lineNumbers: true,
  foldable: true,
  theme: 'bespin',
  renderOnce: false,
  options: {
    scrollbarStyle: 'null',
    mode: 'text/x-yaml',
    lineWrapping: true,
    tabSize: 2,
    smartIndent: true,
    autoCloseBrackets: true,
    matchBrackets: true,
    showCursorWhenSelecting: true,
    foldGutter: {
      rangeFinder: new codemirror.fold.combine(codemirror.fold.indent, codemirror.fold.comment)
    },
    extraKeys: {
      Tab: ctx => { // https://github.com/codemirror/CodeMirror/issues/988
        if (ctx.somethingSelected()) {
          ctx.indentSelection('add');
        }
        else {
          ctx.replaceSelection(Array(ctx.getOption('indentUnit') + 1).join(' '), 'end', '+input');
        }
      },
      'Shift-Tab': ctx => {
        ctx.indentSelection('subtract');
      }
    },
    viewportMargin: Infinity
  }
};

export default CodeMirror;
